import React, {
  ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Autocomplete,
  Button as MUIButton,
  Chip,
  IconButton,
  InputBase,
  Stack,
  TextField,
} from '@mui/material';
import api from '../../services/api';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import {
  DataGridPro,
  GridColDef,
  gridFilterModelSelector,
  GridFooter,
  GridHeaderFilterCellProps,
} from '@mui/x-data-grid-pro';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import {SlidingPaneAddNew} from '../../components/Page/SlidingPaneAddNew';
import {SlidingPaneEdit} from '../../components/Page/SlidingPaneEdit';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import {user as userSchema} from '../../schema/user';
import {datagridColors} from '../../assets/datagridStyles';
import SendIcon from '@mui/icons-material/Send';
import {useGridApiRef} from '@mui/x-data-grid';
import {Header} from './Header';
import {debounce} from 'lodash';
import {styled} from '@mui/material/styles';
import {enumItems} from '../../schema/enumItems';
import {SetRolesDialog} from './UserManagement/SetRolesDialog';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import {DataGridSelectFilter} from '../../components/Table/filters/DataGridSelectFilter';
import {userType} from '../../schema/userType';
import {serviceProvider} from '../../schema/serviceProvider';
import toast from 'react-hot-toast';
import {activityReservation} from '../../schema/activityReservation';
import {salesPartner} from '../../schema/salesPartner';
import {accommodation} from '../../schema/accommodation';
import {activity} from '../../schema/activity';
import {group} from '../../schema/group';
import {serviceReservationViewTable} from '../../schema/serviceReservationViewTable';
import {LoadingButton} from "@mui/lab";

const Search = styled('div')(({theme}) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'rgb(0 0 0 / 5%)',
  '&:hover': {
    backgroundColor: 'rgb(0 0 0 / 7%)',
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const StyledInputBase = styled(InputBase)(({theme}) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}));
const SearchIconWrapper = styled('div')(({theme}) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const UserManagement: ComponentType = () => {
  const [loading, setLoading] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [addNewEntity, setAddNewEntity] = useState(false);
  const [openedSlidingPane, setOpenedSlidingPane] = useState<any>(undefined);
  const [rows, setRows] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<any>(undefined);
  const [selectedApplyOption, setSelectedApplyOption] = useState<any>(
    undefined
  );
  const [selectedRoleFilter, setSelectedRoleFilter] = useState<any>(undefined);
  const apiRef = useGridApiRef();
  const [fastSearchValue, setFastSearchValue] = useState<any>('');
  const [openSetRolesDialog, setOpenSetRolesDialog] = useState<any>(false);
  const debouncedOnChangeSearch = debounce(handleDebouncedOnChangeSearch, 500);

  function handleDebouncedOnChangeSearch(value: any) {
    setFastSearchValue(value);
  }

  useEffect(() => {
    document.title = `Zoe - User Management`;
  }, []);
  const [selectOptions, setSelectOptions] = useState<any>([]);

  const fetchSelectOptions = async (searchText: string) => {
    let string: any = {
      searchValue: searchText,
    };
    const searchParams = new URLSearchParams(string);

    enumItems.userType.client.readAll(1, searchParams).then((result: any) => {
      setSelectOptions(result?.data);
    });
  };

  const fetchUsers = useCallback(() => {
    setLoading(true);

    const searchParams = new URLSearchParams();
    searchParams.set('searchValue', fastSearchValue);
    if (selectedRoleFilter) {
      searchParams.set('roleFilter', JSON.stringify(selectedRoleFilter));
    }

    api.get(`/users`, searchParams).then((res: any) => {
      setRows(res);
      setLoading(false);
    });
  }, [fastSearchValue, selectedRoleFilter]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDeleteClick = (row: any) => {
    setOpenConfirmDialog(row);
  };

  const handleEditClick = (row: any) => {
    setOpenedSlidingPane(row);
  };

  const [checkedRows, setCheckedRows] = useState<any>({});
  const [rowSelectionModel, setRowSelectionModel] = useState<any>([]);

  const fetchApplyChanges = useCallback(() => {
    if (!selectedApplyOption) {
      toast.error(`Error: Select action for apply`);
      return true;
    }

    const sendData: any = {
      option: selectedApplyOption?.value,
      rows: rowSelectionModel,
    };

    if (selectedApplyOption?.value === 'reset_views') {

      const accommodationColumns = Object.keys(accommodation.columns)
        .filter((column) => !accommodation.columns[column].hideInTableView)
        .map((column) => ({
          name: column,
        }));

      const activityReservationColumns = Object.keys(
        activityReservation().columns
      )
        .filter(
          (column) => !activityReservation().columns[column].hideInTableView
        )
        .map((column) => ({
          name: column,
        }));
      const serviceReservationColumns = Object.keys(
        serviceReservationViewTable.columns
      )
        .filter(
          (column) => !serviceReservationViewTable.columns[column].hideInTableView
        )
        .map((column) => ({
          name: column,
        }));

      serviceReservationColumns.unshift({
        name: "__check__",
      });

      const groupColumns = Object.keys(group.columns)
        .filter((column) => !group.columns[column].hideInTableView)
        .map((column) => ({
          name: column,
        }));
      const activityColumns = Object.keys(activity.columns)
        .filter((column) => !activity.columns[column].hideInTableView)
        .map((column) => ({
          name: column,
        }));

      const serviceProviderColumns = Object.keys(serviceProvider.columns)
        .filter((column) => !serviceProvider.columns[column].hideInTableView)
        .map((column) => ({
          name: column,
        }));

      const salesPartnerColumns = Object.keys(salesPartner.columns)
        .filter((column) => !salesPartner.columns[column].hideInTableView)
        .map((column) => ({
          name: column,
        }));

      sendData.default_columns = {
        activity_reservations: activityReservationColumns,
        service_reservations: serviceReservationColumns,
        groups: groupColumns,
        activities: activityColumns,
        accommodations: accommodationColumns,
        service_providers: serviceProviderColumns,
        sales_partners: salesPartnerColumns,
      };
    }

    setLoading(true);

    return api
      .post('/admin/users/apply-option', sendData)
      .then((res: any) => {
        setRowSelectionModel([]);
        toast.success(`Successfully Applied`);
      })
      .catch((error) => {
        let array: any = [];
        for (let errorKey in error) {
          for (let errorMsg in error[errorKey]) {
            array.push(`${error[errorKey][errorMsg]}`);
          }
        }
        toast.error(array.join(','));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedApplyOption, rowSelectionModel]);

  const handleChangeHeaderFilter = (column: string, value: any) => {
    const getDefaultFilter = (field: string) => ({field, operator: 'equals'});

    const state = apiRef.current.state;
    const filterModel = gridFilterModelSelector(state);

    const currentFieldFilters = filterModel.items?.filter(
      ({field}) => field === column
    );

    if (!value) {
      if (currentFieldFilters[0]) {
        apiRef.current.deleteFilterItem(currentFieldFilters[0]);
      }
    } else {
      apiRef.current.upsertFilterItem({
        ...(currentFieldFilters[0] || getDefaultFilter(column)),
        value: value,
      });
    }
  };
  const handleResetViews = () => {

    const accommodationColumns = Object.keys(accommodation.columns)
      .filter((column) => !accommodation.columns[column].hideInTableView)
      .map((column) => ({
        name: column,
      }));

    const activityReservationColumns = Object.keys(
      activityReservation().columns
    )
      .filter(
        (column) => !activityReservation().columns[column].hideInTableView
      )
      .map((column) => ({
        name: column,
      }));
    const serviceReservationColumns = Object.keys(
      serviceReservationViewTable.columns
    )
      .filter(
        (column) => !serviceReservationViewTable.columns[column].hideInTableView
      )
      .map((column) => ({
        name: column,
      }));
    const groupColumns = Object.keys(group.columns)
      .filter((column) => !group.columns[column].hideInTableView)
      .map((column) => ({
        name: column,
      }));
    const activityColumns = Object.keys(activity.columns)
      .filter((column) => !activity.columns[column].hideInTableView)
      .map((column) => ({
        name: column,
      }));


    const serviceProviderColumns = Object.keys(serviceProvider.columns)
      .filter((column) => !serviceProvider.columns[column].hideInTableView)
      .map((column) => ({
        name: column,
      }));

    const salesPartnerColumns = Object.keys(salesPartner.columns)
      .filter((column) => !salesPartner.columns[column].hideInTableView)
      .map((column) => ({
        name: column,
      }));

    api
      .post('/views/reset', {
        activity_reservations: activityReservationColumns,
        service_reservations: serviceReservationColumns,
        groups: groupColumns,
        activities: activityColumns,
        accommodations: accommodationColumns,
        service_providers: serviceProviderColumns,
        sales_partners: salesPartnerColumns,
      })
      .then((res: any) => {
        toast.success(`Successfully Applied`);
      })
      .catch((error) => {
        let array: any = [];
        for (let errorKey in error) {
          for (let errorMsg in error[errorKey]) {
            array.push(`${error[errorKey][errorMsg]}`);
          }
        }
        toast.error(array.join(','));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const columns: GridColDef[] = useMemo(() => {
    const columnArray: GridColDef[] = [
      /*   {
        field: 'checkfield',
        headerName: '#',
        editable: false,
        type: 'actions',
        width: 50,
        maxWidth: 50,
        renderHeader: (props: any) => {
          const id = props?.id;

          return (
            <Checkbox
              onChange={(event, checked) => {
                if (checked) {
                  const checkAllRow: any = {};
                  const allRowId = apiRef.current.getAllRowIds();
                  allRowId.map((rowId: any) => {
                    checkAllRow[rowId] = apiRef.current.getRow(rowId);
                  });
                  setCheckedRows(checkAllRow);
                } else {
                  setCheckedRows({});
                }
                return true;
              }}
              color={'primary'}
              sx={{
                '& > svg': {
                  fill: '#fff !important',
                },
              }}
            />
          );
        },
        getActions: (props: any) => {
          const id = props.id;
          return [
            <Stack>
              <Checkbox
                checked={checkedRows[`${id}`] ? true : false}
                onChange={(event, checked) => {
                  if (checked) {
                    setCheckedRows({ ...checkedRows, [id]: props.row });
                    return true;
                  }

                  const removedRow: any = { ...checkedRows };
                  delete removedRow[id];
                  setCheckedRows(removedRow);

                  return true;
                }}
                color={'primary'}
                sx={{
                  '& > svg': {
                    fill: '#2c65de !important',
                  },
                }}
              />
            </Stack>,
          ];
        },
      },*/
      {
        field: 'name',
        headerName: 'Full Name',
        editable: false,
        type: 'string',
        minWidth: 125,
        flex: 1,
      },
      {
        field: 'phone',
        headerName: 'Phone',
        editable: false,
        type: 'string',
        minWidth: 125,
        flex: 1,
      },
      {
        field: 'email',
        headerName: 'Email',
        editable: false,
        type: 'string',
        minWidth: 125,
        flex: 1,
      },
      {
        field: 'roles',
        headerName: 'Roles',
        editable: false,
        type: 'string',
        minWidth: 125,
        flex: 1,
        renderCell: (props: any) => {
          const id = props.id;
          return (
            <>
              {props?.value && (
                <>
                  {props.value.map((row: any) => {
                    return <Chip label={row?.name}/>;
                  })}
                </>
              )}
            </>
          );
        },
        filterOperators: [
          {
            value: 'equals',
            getApplyFilterFn: (filterItem: any) => {
              if (
                !filterItem.field ||
                !filterItem.value ||
                !filterItem.operator
              ) {
                return null;
              }
              return (params: any): boolean => {
                return params.value?.some(
                  (item: any) => item?.id === filterItem.value?.id
                );
              };
            },
          },
        ],
        renderHeaderFilter: (params: GridHeaderFilterCellProps) => {
          return (
            <>
              <DataGridSelectFilter
                props={params}
                schema={userType}
                onChange={(value) => {
                  handleChangeHeaderFilter('roles', value);
                }}
              />
            </>
          );
        },
      },
      {
        field: 'service_providers',
        headerName: 'Service Providers',
        editable: false,
        type: 'string',
        minWidth: 125,
        flex: 1,
        renderCell: (props: any) => {
          const id = props.id;
          return (
            <>
              {props?.value && (
                <>
                  {props.value.map((row: any) => {
                    return <Chip label={row?.name}/>;
                  })}
                </>
              )}
            </>
          );
        },
        filterOperators: [
          {
            value: 'equals',
            getApplyFilterFn: (filterItem: any) => {
              if (
                !filterItem.field ||
                !filterItem.value ||
                !filterItem.operator
              ) {
                return null;
              }
              return (params: any): boolean => {
                return params.value?.some(
                  (item: any) => item?.id === filterItem.value?.id
                );
              };
            },
          },
        ],
        renderHeaderFilter: (params: GridHeaderFilterCellProps) => {
          return (
            <>
              <DataGridSelectFilter
                props={params}
                schema={serviceProvider}
                onChange={(value) => {
                  handleChangeHeaderFilter('service_providers', value);
                }}

              />
            </>
          );
        },
      },
      {
        field: 'sales_partners',
        headerName: 'Sales Partners',
        editable: false,
        type: 'string',
        minWidth: 125,
        flex: 1,
        renderCell: (props: any) => {
          const id = props.id;
          return (
            <>
              {props?.value && (
                <>
                  {props.value.map((row: any) => {
                    return <Chip label={row?.name}/>;
                  })}
                </>
              )}
            </>
          );
        },
        filterOperators: [
          {
            value: 'equals',
            getApplyFilterFn: (filterItem: any) => {
              if (
                !filterItem.field ||
                !filterItem.value ||
                !filterItem.operator
              ) {
                return null;
              }
              return (params: any): boolean => {
                return params.value?.some(
                  (item: any) => item?.id === filterItem.value?.id
                );
              };
            },
          },
        ],
        renderHeaderFilter: (params: GridHeaderFilterCellProps) => {
          return (
            <>
              <DataGridSelectFilter
                props={params}
                schema={salesPartner}
                onChange={(value) => {
                  handleChangeHeaderFilter('sales_partners', value);
                }}
              />
            </>
          );
        },
      },
      {
        field: 'actions',
        type: 'string',
        headerName: '',
        cellClassName: '',
        editable: false,
        width: 100,
        renderCell: (props: any) => {
          const id = props.id;

          return (
            <>
              <Stack direction={'row'}>
                <IconButton>
                  <EditIcon
                    color={'primary'}
                    onClick={() => {
                      handleEditClick(props.row);
                    }}
                  />
                </IconButton>
                <IconButton>
                  <DeleteIcon
                    color={'error'}
                    onClick={() => {
                      handleDeleteClick(props.row);
                    }}
                  />
                </IconButton>
              </Stack>
            </>
          );
        },
        renderHeaderFilter: () => {
          return <></>;
        },
      },
    ];
    return columnArray;
  }, [checkedRows, rows]);

  useEffect(() => {
    fetchUsers();
  }, [fastSearchValue, selectedRoleFilter]);

  const CustomFooter = () => {
    return (
      <Stack
        direction={'row'}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 8px',
        }}
      >
        <IconButton
          onClick={() => {
            setAddNewEntity(true);
          }}
        >
          <AddIcon color={'primary'}/>
        </IconButton>
        <GridFooter/>
      </Stack>
    );
  };

  function CustomSortIconDesc(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropDownRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  function CustomSortIconAsc(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropUpRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  function CustomSortIcon(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropUpRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  return (
    <>
      <Header
        title={'User Management'}
        rowCount={rows.length}
        hasPagination={false}
      />

      <Stack
        direction="row"
        justifyContent={'space-between'}
        sx={{
          background: '#fff',
          borderRadius: '10px',
          boxShadow: '0px 0px 10px -5px #00000091',
          p: '10px',
          m: '10px',
          '& .MuiStack-root>:not(style)~:not(style)': {
            marginLeft: '0px !important',
          },
        }}
      >
        <Stack
          direction={'row'}
          gap={1}
          alignItems={'center'}
          justifyContent={'start'}
        >
          <Autocomplete
            size={'small'}
            options={[
              {
                label: 'Send activation Email',
                value: 'send_activation_email',
              },
              {
                label: 'Change roles',
                value: 'change_roles',
              },
              {
                label: 'Reset views',
                value: 'reset_views',
              },
            ]}
            sx={{width: 300}}
            onChange={(event, value, reason, details) => {
              setSelectedApplyOption(value);
            }}
            renderInput={(params) => <TextField {...params} label="Action"/>}
          />
          <MUIButton
            disabled={loading}
            onClick={() => {
              console.log(selectedApplyOption);
              if (selectedApplyOption?.value === 'change_roles') {
                setOpenSetRolesDialog(true);
                return true;
              }

              fetchApplyChanges();
              return true;
            }}
            endIcon={<SendIcon/>}
            variant="contained"
          >
            Apply
          </MUIButton>
          {/* <MUIButton
            disabled={loadingResetView}
            onClick={() => {
              handleResetViews();
            }}
            variant="contained"
          >
            Reset Views
          </MUIButton>*/}
        </Stack>
        <Stack direction={'row'} gap={1}>
          <Search>
            <SearchIconWrapper>
              <SearchIcon/>
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search…"
              inputProps={{'aria-label': 'search'}}
              onChange={(event) => {
                debouncedOnChangeSearch(event.target.value);
              }}
            />
          </Search>
        </Stack>
      </Stack>

      <Box sx={datagridColors('calc(100vh - 145px)')}>
        <DataGridPro
          apiRef={apiRef}
          sx={{
            pointerEvents: loading ? 'none' : 'auto',
            opacity: loading ? 0.5 : 1,
            maxHeight: 'calc(100% - 70px)',
          }}
          rows={rows}
          getRowClassName={(params: any) => {
            const color =
              params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
            return `${color}`;
          }}
          disableColumnMenu={true}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          columnHeaderHeight={38}
          rowHeight={38}
          pagination
          slots={{
            // @ts-ignore
            columnSortedDescendingIcon: CustomSortIconDesc,
            columnSortedAscendingIcon: CustomSortIconAsc,
            columnUnsortedIcon: CustomSortIcon,
            footer: CustomFooter,
            headerFilterMenu: null,
          }}
          unstable_headerFilters={true}
          pageSizeOptions={[25, 50, 100, {label: 'All', value: rows.length}]}
          checkboxSelection
          checkboxSelectionVisibleOnly
          onRowSelectionModelChange={(rowSelectionModel, details) => {
            setRowSelectionModel(rowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
        />
      </Box>

      {addNewEntity && (
        <SlidingPaneAddNew
          schema={userSchema}
          id={undefined}
          onSubmit={() => {
            fetchUsers();
            setAddNewEntity(false);
          }}
          propertiesFromTable={{}}
          onClose={(value) => {
            setAddNewEntity(false);
          }}
        />
      )}

      {openedSlidingPane && (
        <SlidingPaneEdit
          schema={userSchema}
          id={openedSlidingPane?.id}
          onSubmit={() => {
            fetchUsers();
            setOpenedSlidingPane(undefined);
          }}
          propertiesFromTable={openedSlidingPane}
          onClose={(value) => {
            setOpenedSlidingPane(undefined);
          }}
        />
      )}

      {openSetRolesDialog && (
        <SetRolesDialog
          checkedRows={checkedRows}
          onClose={() => {
            setOpenSetRolesDialog(false);
          }}
          onSubmit={() => {
            fetchUsers();
            setOpenSetRolesDialog(false);
            setCheckedRows({});
          }}
        />
      )}

      {openConfirmDialog && (
        <>
          <Dialog
            open={true}
            onClose={() => {
              setOpenConfirmDialog(undefined);
            }}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              Are you sure you want to delete it?
            </DialogTitle>
            <DialogContent
              sx={{
                fontSize: {
                  xs: '14px',
                },
              }}
            >
              {`${openConfirmDialog.name} - ${openConfirmDialog.full_name}`}
            </DialogContent>
            <DialogActions>
              <MUIButton
                onClick={() => {
                  setOpenConfirmDialog(undefined);
                }}
              >
                Cancel
              </MUIButton>
              <LoadingButton
                loading={loadingButton}
                variant={'contained'}
                color={'error'}
                onClick={() => {
                  setLoadingButton(true);
                  userSchema.client
                    .delete(openConfirmDialog.id as number, {})
                    .then((res: any) => {
                      setOpenConfirmDialog(undefined);
                      fetchUsers();
                    })
                    .catch((e: any) => {
                    }).finally(() => {
                    setLoadingButton(false);
                  });
                }}
                autoFocus
              >
                Delete
              </LoadingButton>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
};
